import {
  EndTournamentEventParams,
  SaykitEventType,
  StartPuzzleEventParams,
  StartGameEventParams,
  CompleteGameEventParams,
  CompletePuzzleEventParams,
  IncomeCoinsEventParams,
  OutcomeCoinsEventParams,
  InGamePurchaseEventParams,
  EventParams,
  AppLoadedEventParams,
  AdsenseEventParams,
  ShareEventParams,
  CreateTournamentEventParams,
  UTMEventParams,
  PerformanceEventParams,
  GameErrorEventParams,
  TutorialEventParams,
  TournamentEventParams,
  TournamentStartGameEventParams,
  TournamentCompleteGameEventParams,
  TournamentsEventParams,
  TournamentLobbyEventParams,
} from './type';

const getExtraForAdsenseEvent = ({ type, placement }: AdsenseEventParams) => {
  if (type === 'adsense') {
    return JSON.stringify({ class: type });
  }

  return JSON.stringify({ class: type, placeId: placement });
};

export const MAIN_TO_SAYKIT_EVENTS: Record<string, (options: any) => SaykitEventType> = {
  wx_Core_startGame: ({ gameNumber, mode, ...options }: StartGameEventParams) => ({
    event: 'level_started',
    param1: Number(gameNumber),
    tag: mode,
    extra: JSON.stringify(options),
  }),
  wx_Core_completeGame: ({ gameNumber, mode, ...options }: CompleteGameEventParams) => ({
    event: 'level_completed',
    param1: Number(gameNumber),
    tag: mode,
    extra: JSON.stringify(options),
  }),
  wx_Core_startPuzzle: ({ number, category }: StartPuzzleEventParams) => ({
    event: 'level_extra_started',
    param1: Number(number),
    tag: category,
  }),
  wx_Core_completePuzzle: ({ number, category, ...options }: CompletePuzzleEventParams) => ({
    event: 'level_extra_completed',
    param1: Number(number),
    tag: category,
    extra: JSON.stringify(options),
  }),
  wx_Coins_income: ({ amount, place, total }: IncomeCoinsEventParams) => ({
    event: 'soft_income',
    param1: Number(amount),
    param2: Number(total),
    tag: place,
  }),
  wx_Coins_outcome: ({ amount, place, total }: OutcomeCoinsEventParams) => ({
    event: 'soft_outcome',
    param1: Number(amount),
    param2: Number(total),
    tag: place,
  }),
  wx_Purchases_inGame: ({ id }: InGamePurchaseEventParams) => ({
    event: 'item',
    param2: 3, // soft currency
    tag: id,
  }),
  wx_Views_mainMenu: () => ({ event: 'screen', tag: 'onboarding-screen' }),
  wx_Views_mainMenuGame: () => ({ event: 'screen', tag: 'onboarding-game-screen' }),
  wx_Views_clubsScreen: () => ({ event: 'screen', tag: 'clubs-screen' }),
  wx_Views_skillLevel: () => ({ event: 'screen', tag: 'skill-level-screen' }),
  wx_Views_Shop: () => ({ event: 'screen', tag: 'shop-screen' }),
  wx_Views_Challenges: () => ({ event: 'screen', tag: 'challenges-screen' }),
  wx_Views_Ranking: () => ({ event: 'screen', tag: 'leaderboard-screen' }),
  wx_Views_Learn: () => ({ event: 'screen', tag: 'lessons-screen' }),
  wx_Views_Settings: () => ({ event: 'screen', tag: 'settings-screen' }),
  wx_Views_watchProfile: () => ({ event: 'screen', tag: 'profile-screen' }),
  wx_Views_Tournaments: ({ count }: TournamentsEventParams) => ({
    event: 'screen',
    tag: 'tournaments-screen',
    extra: count ? JSON.stringify({ count }) : undefined,
  }),
  wx_Views_TournamentLobby: (params: TournamentLobbyEventParams) => ({
    event: 'screen',
    tag: 'tournament-lobby-screen',
    extra: JSON.stringify(params),
  }),
  wx_Views_PlayWithFriends: () => ({ event: 'screen', tag: 'play-with-friends-screen' }),
  wx_Views_analysis: () => ({ event: 'screen', tag: 'analysis' }),
  wx_Views_tutorial: ({ step, time }: TutorialEventParams) => ({
    event: 'wx_Views_tutorial',
    param1: Number(step),
    extra: time ? JSON.stringify({ time }) : undefined,
  }),
  wx_Taps_play: () => ({ event: 'click', tag: 'play', extra: 'onboarding-screen' }),
  wx_Taps_playClubScreen: () => ({ event: 'click', tag: 'play', extra: 'clubs-screen' }),
  wx_Taps_connect: ({ type }: EventParams) => ({ event: 'click', tag: 'connect', extra: type }),
  wx_Taps_rewardedVCoins: () => ({ event: 'click', tag: 'get', extra: 'free-coins-popup' }),
  wx_Taps_mobileApp: (options) => ({ event: 'click', tag: 'app-banner', extra: JSON.stringify(options) }),
  wx_Taps_Shop: () => ({ event: 'click', tag: 'shop' }),
  wx_Taps_Learn: () => ({ event: 'click', tag: 'learn' }),
  wx_Taps_Ranking: () => ({ event: 'click', tag: 'leaderboard' }),
  wx_Taps_Settings: () => ({ event: 'click', tag: 'settings' }),
  wx_Taps_Feedback: () => ({ event: 'click', tag: 'feedback' }),
  wx_Taps_puzzles: () => ({ event: 'click', tag: 'puzzles' }),
  wx_Taps_navigateToAnalysis: () => ({ event: 'click', tag: 'navigate-to-analysis' }),
  wx_Taps_deeplink: () => ({ event: 'click', tag: 'deeplink' }),
  wx_Taps_analyzeGame: () => ({ event: 'click', tag: 'analyze-game' }),
  wx_Views_appLoaded: ({ time }: AppLoadedEventParams) => ({ event: 'app_loaded', extra: time }),
  wx_Views_appUnloaded: ({ time }: AppLoadedEventParams) => ({ event: 'wx_Views_appUnloaded', extra: time }),
  wx_Ads_showedInterstitial: ({ type, placement, name }: AdsenseEventParams) => {
    return {
      event: 'interstitial_imp',
      tag: name,
      extra: getExtraForAdsenseEvent({ type, placement }),
    };
  },
  wx_Ads_showedReward: ({ type, placement, name }: AdsenseEventParams) => ({
    event: 'rewarded_imp',
    tag: name,
    extra: getExtraForAdsenseEvent({ type, placement }),
  }),
  wx_Ads_showedBanner: ({ type, placement, name }: AdsenseEventParams) => ({
    event: 'banner_imp',
    tag: name,
    extra: getExtraForAdsenseEvent({ type, placement }),
  }),
  wx_Instant_CrossPromotion: () => ({ event: 'wx_Instant_CrossPromotion' }),
  wx_Viral_shareResult: ({ mode }: ShareEventParams) => ({ event: 'wx_Viral_shareResult', tag: mode }),
  wx_Tournament_create: ({ time }: CreateTournamentEventParams) => ({
    event: 'wx_Tournament_create',
    param1: Number(time),
  }),
  wx_Tournament_join: () => ({ event: 'wx_Tournament_join' }),
  wx_Tournament_end: (options: EndTournamentEventParams) => ({
    event: 'wx_Tournament_end',
    extra: JSON.stringify(options),
  }),
  wx_Auth_signUp: () => ({ event: 'wx_Auth_signUp' }),
  wx_Core_2games: () => ({ event: 'wx_Core_2games' }),
  wx_Core_5games: () => ({ event: 'wx_Core_5games' }),
  wx_Core_10games: () => ({ event: 'wx_Core_10games' }),
  wx_Core_gameError: ({ mode, ...options }: GameErrorEventParams) => ({
    event: 'wx_Core_gameError',
    tag: mode,
    extra: JSON.stringify(options),
  }),
  wx_Core_gameReconnected: ({ time }) => ({ event: 'wx_Core_gameReconnected', extra: time }),
  wx_UTM_sent: ({ attributes }: UTMEventParams) => ({ event: 'wx_UTM_sent', extra: attributes }),
  wx_UTM_failed: ({ attributes }: UTMEventParams) => ({ event: 'wx_UTM_failed', extra: attributes }),
  wx_Perf_CLS: (params: PerformanceEventParams) => ({
    event: 'wx_Perf_CLS',
    extra: JSON.stringify(params),
  }),
  wx_Perf_FID: (params: PerformanceEventParams) => ({
    event: 'wx_Perf_FID',
    extra: JSON.stringify(params),
  }),
  wx_Perf_LCP: (params: PerformanceEventParams) => ({
    event: 'wx_Perf_LCP',
    extra: JSON.stringify(params),
  }),
  wx_Perf_FCP: (params: PerformanceEventParams) => ({
    event: 'wx_Perf_FCP',
    extra: JSON.stringify(params),
  }),
  wx_Perf_TTFB: (params: PerformanceEventParams) => ({
    event: 'wx_Perf_TTFB',
    extra: JSON.stringify(params),
  }),
  wx_Taps_tournaments: () => ({
    event: 'click',
    tag: 'tournaments',
  }),
  wx_Taps_tournamentLobby: (params: TournamentEventParams) => ({
    event: 'click',
    tag: 'tournament-lobby',
    extra: JSON.stringify(params),
  }),
  wx_Tournaments_startGame: (params: TournamentStartGameEventParams) => ({
    event: 'wx_Tournaments_startGame',
    extra: JSON.stringify(params),
  }),
  wx_Tournaments_completeGame: (params: TournamentCompleteGameEventParams) => ({
    event: 'wx_Tournaments_completeGame',
    extra: JSON.stringify(params),
  }),
};

export const AUTH_EVENTS = {
  signUp: 'wx_Auth_signUp',
};

export const CORE_EVENTS = {
  // Core
  startGame: 'wx_Core_startGame',
  completeGame: 'wx_Core_completeGame',
  startGameWithBot: 'wx_Core_startGameWithBot',
  completeGameWithBot: 'wx_Core_completeGameWithBot',
  useHint: 'wx_Core_useHint',
  gameError: 'wx_Core_gameError',
  twoGames: 'wx_Core_2games',
  fiveGames: 'wx_Core_5games',
  tenGames: 'wx_Core_10games',

  // Puzzles
  startPuzzle: 'wx_Core_startPuzzle',
  completePuzzle: 'wx_Core_completePuzzle',
} as const;

export const VIEW_EVENTS = {
  mainMenu: 'wx_Views_mainMenu',
  mainMenuGame: 'wx_Views_mainMenuGame',
  appLoaded: 'wx_Views_appLoaded',
  tutorial: 'wx_Views_tutorial',
  analysis: 'wx_Views_analysis',
};

export const ADS_EVENTS = {
  bannerFailed: 'wx_Ads_bannerFailed',
  showedBanner: 'wx_Ads_showedBanner',
  interstitialFailed: 'wx_Ads_interstitialFailed',
  showedInterstitial: 'wx_Ads_showedInterstitial',
} as const;

export const TAP_EVENTS = {
  mainMenuPlay: 'wx_Taps_play',
  continueOnboarding: 'wx_Taps_continueOnboarding',
  surrender: 'wx_Taps_surrender',
  exitMatch: 'wx_Taps_exitMatch',
  analyzeGame: 'wx_Taps_analyzeGame',
  deeplink: 'wx_Taps_deeplink',
};

export const UTM_EVENTS = {
  sent: 'wx_UTM_sent',
  failed: 'wx_UTM_failed',
};

export const PERFORMANCE_EVENTS = {
  cls: 'wx_Perf_CLS',
  fid: 'wx_Perf_FID',
  lcp: 'wx_Perf_LCP',
  fcp: 'wx_Perf_FCP',
  ttfb: 'wx_Perf_TTFB',
};
